export type ServiceTicketStatus = string;

export const DEFAULT_SERVICE_TICKET_STATUS_KEYS = {
  ASSIGNED: 'assigned',
  IN_PROGRESS: 'in_progress',
  RESOLVED: 'resolved',
  CLOSED: 'closed',
} as const;

export type ServiceTicketUrgency = 'normal' | 'escalated' | 'critical';

export type ServiceTicketScope = 'all' | 'mine' | 'watching' | 'team' | 'unclaimed' | 'attention';

export type ServiceTicketActionType =
  | 'created'
  | 'assigned'
  | 'reassigned'
  | 'note_added'
  | 'escalated'
  | 'resolved'
  | 'reopened'
  | 'status_changed'
  | 'resolution_saved'
  | 'updated'
  | 'claimed'
  | 'guest_reply'
  | 'attention_handled'
  | 'evidence_added'
  | 'comment';

export interface ServiceTicketAssignee {
  id: string;
  name: string;
  initials: string;
}

export interface ServiceTicketActionEntry {
  id: string;
  type: ServiceTicketActionType;
  actor: ServiceTicketAssignee;
  at: string;
  note?: string;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
}

export interface ServiceTicketNote {
  id: string;
  author: ServiceTicketAssignee;
  at: string;
  body: string;
}

export type TicketFeedbackType = 'complaint' | 'compliment' | 'general' | 'risk_assurance';

export type CompensationType = 'upgrades' | 'addons' | 'refund' | 'evoucher';

export type GuestResolutionSentiment =
  | 'satisfied_with_comp'
  | 'satisfied_no_comp'
  | 'dissatisfied_with_comp'
  | 'dissatisfied_no_comp'
  | 'na';

export type CsatStatus = 'yes' | 'no' | 'na';

export interface CompensationValues {
  upgrades?: number | null;
  addons?: number | null;
  refund?: number | null;
  evoucher?: number | null;
}

export interface TicketResolution {
  feedbackType: TicketFeedbackType | null;
  dateOfFirstReply: string | null;
  dateOfResolution: string | null;
  compensationGiven: boolean;
  compensationTypes: readonly CompensationType[];
  compensationValues: CompensationValues;
  sentiment: GuestResolutionSentiment | null;
  mdCeoLetter: boolean;
  closingNotes: string;
  csat: CsatStatus | null;
  guestContacted?: CsatStatus | null;
  csatSurveySentAt?: string | null;
  csatResponseId?: string | null;
  currency?: string;
  voucherReference?: string;
  updatedAt: string;
  updatedBy: ServiceTicketAssignee;
}

export type TicketResolutionPayload = Omit<TicketResolution, 'updatedAt' | 'updatedBy'>;

export type TicketWorkflow = 'central_complaint' | 'in_the_moment';
export type TicketIntakeChannel = 'form' | 'letter' | 'email' | 'phone' | 'in_person';

export interface TicketCaseContext {
  organizationId: string;
  accountId: string;
  propertyId: string;
  workflow: TicketWorkflow;
  intakeChannel: TicketIntakeChannel;
  questionnaireId: string | null;
  submissionId: string | null;
  guestId: string | null;
  guestEmail: string;
  guestPhone: string;
  bookingReference: string;
  initialFeedbackType: TicketFeedbackType | null;
  issueCategories: readonly string[];
}

export interface TicketQueue {
  id: string;
  organizationId: string;
  accountIds: readonly string[];
  name: string;
  memberIds: readonly string[];
  defaultOwnerId: string | null;
}

export interface TicketInboundMessage {
  ticketId: string;
  conversationId: string;
  messageId: string;
  receivedAt: string;
  preview: string;
}

export interface TicketResponseEvent {
  ticketId: string;
  conversationId: string;
  messageId: string;
  sentAt: string;
  handledInboundMessageIds: readonly string[];
}

export interface TicketSlaBreachEvent {
  ticketId: string;
  target: 'first_response' | 'resolution';
  deadline: string;
  occurredAt: string;
}

export interface TicketEvidence {
  id: string;
  name: string;
  url: string;
  kind: 'link';
  addedAt: string;
  addedBy: ServiceTicketAssignee;
}

export interface TicketResolutionHistory {
  closedAt: string;
  status: string;
  resolution: TicketResolution;
}

export interface ServiceTicket extends TicketCaseContext {
  id: string;
  version: number;
  updatedAt: string;
  submittedBy: ServiceTicketAssignee;
  queueId: string | null;
  parentTicketId: string | null;
  caseId: string;
  conversationId: string | null;
  inboundMessages: readonly TicketInboundMessage[];
  readMessageIdsByUser: Readonly<Record<string, readonly string[]>>;
  needsResponse: boolean;
  handledMessageIds: readonly string[];
  lastGuestReplyAt: string | null;
  firstResponseDueAt: string;
  escalationRecipientId: string | null;
  escalationRoleLabel: string;
  escalationKeys: readonly string[];
  resolutionHistory: readonly TicketResolutionHistory[];
  evidence: readonly TicketEvidence[];
  reviewId: string | null;
  subject: string;
  status: ServiceTicketStatus;
  urgency: ServiceTicketUrgency;
  departmentId: string | null;
  assignee: ServiceTicketAssignee | null;
  guestName: string;
  rating: number | null;
  actionBefore: string;
  createdAt: string;
  resolvedAt: string | null;
  watcherIds: readonly string[];
  isMine: boolean;
  isWatching: boolean;
  lastActionNote: string | null;
  resolveOnResponse?: boolean;
  resolution?: TicketResolution;
  actions: readonly ServiceTicketActionEntry[];
  notes: readonly ServiceTicketNote[];
}

export interface ServiceTicketDraft extends Partial<TicketCaseContext> {
  guestName?: string;
  queueId?: string | null;
  parentTicketId?: string | null;
  expectedVersion?: number;
  subject: string;
  assigneeId: string | null;
  watcherIds: readonly string[];
  actionBefore: string;
  note: string;
  status: ServiceTicketStatus;
  urgency?: ServiceTicketUrgency;
  departmentId?: string | null;
  resolveOnResponse?: boolean;
}

export interface ServiceTicketDateRange {
  from: string;
  to: string;
}

export interface ServiceTicketQuery {
  organizationId?: string;
  accountIds?: readonly string[];
  modifiedSince?: string;
  first: number;
  rows: number;
  sortField: string | null;
  sortOrder: 'asc' | 'desc' | null;
  search: string;
  filters: Record<string, string[]>;
  scope: ServiceTicketScope;
  dateRange?: ServiceTicketDateRange | null;
}

export interface TicketSubmission extends TicketCaseContext {
  submittedBy?: ServiceTicketAssignee;
  submissionId: string;
  questionnaireId: string;
  guestName: string;
  subject: string;
  body: string;
  scores: Readonly<Record<string, number>>;
  conversationId: string | null;
}

export interface TicketExport {
  schemaVersion: 1;
  exportedAt: string;
  query: ServiceTicketQuery;
  total: number;
  tickets: readonly ServiceTicket[];
}

export interface ServiceTicketPage {
  data: ServiceTicket[];
  total: number;
}

export interface ServiceTicketCounts {
  total: number;
  open: number;
  escalated: number;
  dueToday: number;
  overdue: number;
  resolvedThisWeek: number;
  avgResolutionHours: number;
}

export interface ServiceTicketBoardColumn {
  status: ServiceTicketStatus;
  tickets: readonly ServiceTicket[];
}

export interface ServiceTicketBoard {
  columns: readonly ServiceTicketBoardColumn[];
  total: number;
}
